import Header from "../layout/Header";
import Footer from "../layout/Footer";
import SEO from "../SEO/SEO"

const PrivacyPolicy = () => {
  return (
    <>
      <Header />
      <SEO
        title="Privacy Policy | Nexora Machinery"
        description="Learn how Nexora Machinery protects your privacy and handles your personal information when you use our services and purchase our industrial products."
        keywords="About Nexora Machinery, CNC Machine Supplier"
        path="/PrivacyPolicy"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white pt-24 pb-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            Privacy <span className="text-cyan-400">Policy</span>
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-6">
            How We Protect Your Information
          </p>
          <div className="w-20 h-1 bg-cyan-400 mx-auto"></div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-lg prose-slate max-w-none">
            <div className="bg-slate-50 rounded-xl p-6 mb-8">
              <p className="text-slate-600 text-lg">
                <strong>Last Updated:</strong> December 2023
                <br />
                This Privacy Policy describes how Nexora Machinery collects,
                uses, and protects your information.
              </p>
            </div>

            <div className="space-y-12">
              {/* Information Collection */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  1. Information We Collect
                </h2>
                <div className="space-y-4 text-slate-600">
                  <h3 className="text-lg font-semibold text-slate-700">
                    Personal Information
                  </h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>
                      Contact details (name, email, phone number, company)
                    </li>
                    <li>
                      Business information (company name, address, tax ID)
                    </li>
                    <li>Purchase history and order details</li>
                    <li>
                      Payment information (processed securely through our
                      payment partners)
                    </li>
                    <li>
                      Communication records (emails, inquiries, support tickets)
                    </li>
                  </ul>

                  <h3 className="text-lg font-semibold text-slate-700 mt-6">
                    Technical Information
                  </h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>IP address and browser type</li>
                    <li>Website usage data and analytics</li>
                    <li>Device information and cookies</li>
                  </ul>
                </div>
              </section>

              {/* How We Use Information */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  2. How We Use Your Information
                </h2>
                <div className="grid md:grid-cols-2 gap-6 text-slate-600">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-700 mb-2">
                      Business Operations
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-sm ml-2">
                      <li>Process orders and transactions</li>
                      <li>Provide product information and quotes</li>
                      <li>Manage customer accounts</li>
                      <li>Handle warranty claims</li>
                    </ul>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-700 mb-2">
                      Customer Service
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-sm ml-2">
                      <li>Respond to inquiries</li>
                      <li>Provide technical support</li>
                      <li>Send service updates</li>
                      <li>Manage maintenance schedules</li>
                    </ul>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-700 mb-2">
                      Marketing & Communication
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-sm ml-2">
                      <li>Send product updates (with consent)</li>
                      <li>Share industry news</li>
                      <li>Inform about new machinery</li>
                      <li>Promote special offers</li>
                    </ul>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-700 mb-2">
                      Legal & Compliance
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-sm ml-2">
                      <li>Meet regulatory requirements</li>
                      <li>Process warranty claims</li>
                      <li>Maintain business records</li>
                      <li>Ensure product safety</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Data Sharing */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  3. Information Sharing
                </h2>
                <div className="text-slate-600 space-y-4">
                  <p>We may share your information with:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>
                      <strong>Shipping Partners:</strong> For order delivery and
                      logistics
                    </li>
                    <li>
                      <strong>Payment Processors:</strong> For secure
                      transaction handling
                    </li>
                    <li>
                      <strong>Manufacturers:</strong> For warranty registration
                      and technical support
                    </li>
                    <li>
                      <strong>Service Providers:</strong> For website analytics
                      and customer support
                    </li>
                    <li>
                      <strong>Legal Authorities:</strong> When required by law
                      or to protect our rights
                    </li>
                  </ul>
                  <p className="text-sm bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <strong>Note:</strong> We never sell your personal
                    information to third parties for marketing purposes.
                  </p>
                </div>
              </section>

              {/* Data Protection */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  4. Data Security
                </h2>
                <div className="text-slate-600 space-y-4">
                  <p>We implement appropriate security measures including:</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <h3 className="font-semibold text-green-700 mb-2">
                        Technical Measures
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-sm ml-2">
                        <li>SSL encryption</li>
                        <li>Secure servers</li>
                        <li>Regular security updates</li>
                        <li>Access controls</li>
                      </ul>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <h3 className="font-semibold text-green-700 mb-2">
                        Organizational Measures
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-sm ml-2">
                        <li>Staff training</li>
                        <li>Privacy policies</li>
                        <li>Data access limits</li>
                        <li>Regular audits</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Your Rights */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  5. Your Rights
                </h2>
                <div className="text-slate-600 space-y-4">
                  <p>You have the right to:</p>
                  <div className="bg-blue-50 rounded-xl p-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <h3 className="font-semibold text-blue-700 mb-3">
                          Access & Control
                        </h3>
                        <ul className="list-disc list-inside space-y-2 text-sm ml-2">
                          <li>Access your personal data</li>
                          <li>Correct inaccurate information</li>
                          <li>Request data deletion</li>
                          <li>Object to data processing</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-semibold text-blue-700 mb-3">
                          Communication
                        </h3>
                        <ul className="list-disc list-inside space-y-2 text-sm ml-2">
                          <li>Opt-out of marketing</li>
                          <li>Withdraw consent</li>
                          <li>Request data portability</li>
                          <li>Lodge complaints</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Contact */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  6. Contact Us
                </h2>
                <div className="bg-slate-50 rounded-xl p-6 text-slate-600">
                  <p className="mb-4">
                    For privacy-related questions or to exercise your rights,
                    contact our Data Protection Officer:
                  </p>
                  <div className="space-y-2">
                    <p>
                      <strong>Email:</strong> privacy@nexora.com
                    </p>
                    <p>
                      <strong>Phone:</strong> +1 (555) 123-4567
                    </p>
                    <p>
                      <strong>Address:</strong> Nexora Machinery,
                      6/11,Palarpathi, Kinathukadavu,Coimbatore,
                      Tanilnadu-642109
                    </p>
                  </div>
                  <p className="mt-4 text-sm text-slate-500">
                    We respond to all legitimate requests within 30 days.
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default PrivacyPolicy;
